import React, { createContext, useContext, ReactNode } from "react";

import { useProductFormState } from "@/hooks/use-product-form-state";
import { ProductFormState } from "@/components/screens/ProductFormPage/types";
import { ProductFormPageMessages } from "@/messages/products/product-form-page.messages";
import { IProductImage } from "@/interfaces/product-image.interface";

type ProductFormStateContextType = {
  state: ProductFormState;
  saveProduct: () => Promise<number | null>;
  uploadImages: (images: IProductImage[], productId: number) => Promise<void>;
  ensureProductSaved: () => Promise<boolean>;
  canOpenSkuModal: () => boolean;
  getDisabledTooltip: () => string;
  isLoading: boolean;
  savedImages: IProductImage[];
  reloadImages: () => Promise<void>;
  imagesToDelete: string[];
  setImagesToDelete: React.Dispatch<React.SetStateAction<string[]>>;
};

const ProductFormStateContext = createContext<ProductFormStateContextType | undefined>(undefined);

type ProductFormStateProviderProps = {
  children: ReactNode;
  messages: ProductFormPageMessages;
  customerId: number;
  isEditing: boolean;
};

export const ProductFormStateProvider: React.FC<ProductFormStateProviderProps> = ({
  children,
  messages,
  customerId,
  isEditing,
}) => {
  const productFormState = useProductFormState({
    messages,
    customerId,
    isEditing,
  });

  return (
    <ProductFormStateContext.Provider value={productFormState}>
      {children}
    </ProductFormStateContext.Provider>
  );
};

export const useProductFormStateContext = (): ProductFormStateContextType => {
  const context = useContext(ProductFormStateContext);
  if (!context) {
    throw new Error("useProductFormStateContext must be used within ProductFormStateProvider");
  }
  return context;
};
